import { List } from "immutable";
import { PropNames } from "@showitinc/merge-utils";
import AppConstants from "@/constants/AppConstants";
import {
  FORM_FIELD_ARROW_STYLE_CARET,
  FORM_FIELD_ARROW_STYLE_CHEVRON,
  FORM_FIELD_ARROW_STYLE_NONE,
  FORM_FIELD_BORDER_STYLE_DASHED,
  FORM_FIELD_BORDER_STYLE_DOTTED,
  FORM_FIELD_BORDER_STYLE_NONE,
  FORM_FIELD_BORDER_STYLE_SOLID,
  FORM_FIELD_CHECKED_STYLE_CHECKMARK,
  FORM_FIELD_CHECKED_STYLE_FILLED,
  FORM_FIELD_CHECKED_STYLE_OUTLINE,
  FORM_FIELD_CHECKED_STYLE_X,
  FORM_FIELD_LABEL_POSITION_ABOVE,
  FORM_FIELD_LABEL_POSITION_INLINE,
  FORM_FIELD_RADIO_LAYOUT_HORIZONTAL,
  FORM_FIELD_RADIO_LAYOUT_VERTICAL,
  FORM_FIELD_REQUIRED_PLACEMENT_AFTER,
  FORM_FIELD_REQUIRED_PLACEMENT_BEFORE,
  FORM_STATE_DEFAULT,
  FORM_STATE_ERROR,
  FORM_STATE_FOCUS,
  FORM_STYLE_CHECKBOXES,
  FORM_STYLE_DROPDOWN,
  FORM_STYLE_FIELD,
  FORM_STYLE_IDS,
  FORM_STYLE_RADIOS,
} from "@/constants/FormFieldConstants.mjs";
import { SYNCED_PROPS } from "@/constants/DesignConstants.mjs";
import {
  createFormStyleDefaults,
  DEFAULT_FORM_FONT,
  FORM_FALLBACK_FONT,
} from "@/constants/FormStyleDefaults.mjs";
import {
  BORDER_RADIUS,
  SHADOW_BLUR,
  SHADOW_COLOR,
  SHADOW_OFFSET_X,
  SHADOW_OFFSET_Y,
  SHADOW_STYLE,
} from "@/constants/MergeConstants";
import { BASE_INPUT_CONFIG } from "@/utils/Element/BaseInputConfigMap";
import { mapProp } from "@/utils/Element/BasePropertyMapper";
import { resolveFormFieldValue } from "@/utils/FormFieldStyleResolver";
import { ensureArray } from "@/utils/JSUtils";
import {
  canPropInherit,
  getProp,
  getSubParent,
  inheritProp,
  isInheritedPropSynced,
  isPropSynced,
} from "@/utils/Merge/MergeUtils.mjs";
import { initFormatter } from "@/utils/PropertyFormatters";
import { ALL_HANDLERS } from "./ElementInputConstants.mjs";

const {
  CONTROL_ARROW_COLOR,
  CONTROL_ARROW_SIZE,
  CONTROL_ARROW_STYLE,
  CONTROL_BACKGROUND_COLOR,
  CONTROL_BORDER_COLOR,
  CONTROL_BORDER_RADIUS,
  CONTROL_BORDER_STYLE,
  CONTROL_BORDER_WIDTH,
  CONTROL_CHECKBOX_STYLE,
  CONTROL_PADDING,
  CONTROL_RADIO_DOT_COLOR,
  CONTROL_RADIO_DOT_SIZE,
  CONTROL_RADIO_STYLE,
  CONTROL_SHADOW_BLUR,
  CONTROL_SHADOW_COLOR,
  CONTROL_SHADOW_OFFSET_X,
  CONTROL_SHADOW_OFFSET_Y,
  CONTROL_SHADOW_STYLE,
  CONTROL_TEXT_COLOR,
  CONTROL_TEXT_FONT,
  CONTROL_TEXT_PLACEHOLDER_COLOR,
  CONTROL_TEXT_SIZE,
  LABEL_COLOR,
  LABEL_FONT,
  LABEL_GAP,
  LABEL_POSITION,
  LABEL_REQUIRED_COLOR,
  LABEL_REQUIRED_PLACEMENT,
  LABEL_SIZE,
  LABEL_WIDTH,
  RADIO_DIRECTION,
  RADIO_GAP,
} = PropNames;

const { LAYOUT_DESKTOP, LAYOUT_MOBILE, THEME_STYLES } = AppConstants;

export const FORM_STATE_OPTIONS = [
  { value: FORM_STATE_DEFAULT, label: "Default" },
  { value: FORM_STATE_FOCUS, label: "Focus" },
  { value: FORM_STATE_ERROR, label: "Error" },
];

export const LABEL_POSITION_OPTIONS = [
  { value: FORM_FIELD_LABEL_POSITION_ABOVE, label: "Above" },
  { value: FORM_FIELD_LABEL_POSITION_INLINE, label: "Inline" },
];

export const REQUIRED_PLACEMENT_OPTIONS = [
  { value: FORM_FIELD_REQUIRED_PLACEMENT_AFTER, label: "After label" },
  { value: FORM_FIELD_REQUIRED_PLACEMENT_BEFORE, label: "Before label" },
];

export const BORDER_STYLE_OPTIONS = [
  { value: FORM_FIELD_BORDER_STYLE_SOLID, label: "Solid" },
  { value: FORM_FIELD_BORDER_STYLE_DASHED, label: "Dashed" },
  { value: FORM_FIELD_BORDER_STYLE_DOTTED, label: "Dotted" },
  { value: FORM_FIELD_BORDER_STYLE_NONE, label: "None" },
];

export const ARROW_STYLE_OPTIONS = [
  { value: FORM_FIELD_ARROW_STYLE_CHEVRON, label: "Chevron" },
  { value: FORM_FIELD_ARROW_STYLE_CARET, label: "Caret" },
  { value: FORM_FIELD_ARROW_STYLE_NONE, label: "None" },
];

export const RADIO_LAYOUT_OPTIONS = [
  { value: FORM_FIELD_RADIO_LAYOUT_VERTICAL, label: "Vertical" },
  { value: FORM_FIELD_RADIO_LAYOUT_HORIZONTAL, label: "Horizontal" },
];

export const RADIO_CHECKED_STYLE_OPTIONS = [
  { value: FORM_FIELD_CHECKED_STYLE_FILLED, label: "Filled" },
  { value: FORM_FIELD_CHECKED_STYLE_OUTLINE, label: "Outline" },
];

export const CHECKBOX_CHECKED_STYLE_OPTIONS = [
  { value: FORM_FIELD_CHECKED_STYLE_CHECKMARK, label: "Checkmark" },
  { value: FORM_FIELD_CHECKED_STYLE_X, label: "X" },
];

/**
 * A control taken from the shared config, minus its handler list: those are wired through the
 * element panel's updater, which this panel has not got — `buildFormFieldPropData` wires its own.
 * @param {string} configKey - A `BASE_INPUT_CONFIG` key.
 * @param {Object=} overrides
 * @returns {Object} Control meta.
 */
const sharedControl = (configKey, overrides) => ({
  ...BASE_INPUT_CONFIG[configKey],
  inputEventHandlers: undefined,
  ...overrides,
});

const FORM_FIELD_STRUCTURAL_PROPS = [
  LABEL_POSITION,
  LABEL_REQUIRED_PLACEMENT,
  LABEL_GAP,
  LABEL_WIDTH,
  CONTROL_ARROW_STYLE,
  RADIO_DIRECTION,
  RADIO_GAP,
  CONTROL_RADIO_STYLE,
  CONTROL_CHECKBOX_STYLE,
];

const BASE_PROPERTY_META_MAP = {
  // label
  [LABEL_POSITION]: {
    label: "Label Position",
    type: "select",
    options: LABEL_POSITION_OPTIONS,
  },
  [LABEL_REQUIRED_PLACEMENT]: {
    label: "Required Mark",
    type: "select",
    options: REQUIRED_PLACEMENT_OPTIONS,
  },
  [LABEL_GAP]: { label: "Label Gap", type: "number", min: 0, units: "px" },
  [LABEL_WIDTH]: { label: "Label Width", type: "number", min: 0, units: "px" },
  [LABEL_FONT]: { label: "Label Font", type: "font" },
  [LABEL_SIZE]: { label: "Label Size", type: "number", min: 0, units: "px" },
  [LABEL_COLOR]: { label: "Label Color", type: "color" },
  [LABEL_REQUIRED_COLOR]: { label: "Required Color", type: "color" },
  // input field text
  [CONTROL_TEXT_FONT]: { label: "Font", type: "font" },
  [CONTROL_TEXT_SIZE]: { label: "Size", type: "number", min: 0, units: "px" },
  [CONTROL_TEXT_COLOR]: { label: "Color", type: "color" },
  [CONTROL_TEXT_PLACEHOLDER_COLOR]: { label: "Placeholder", type: "color" },
  // input field container
  [CONTROL_BACKGROUND_COLOR]: { label: "Background", type: "color" },
  [CONTROL_BORDER_STYLE]: { label: "Border", type: "select", options: BORDER_STYLE_OPTIONS },
  [CONTROL_BORDER_COLOR]: { label: "Border Color", type: "color" },
  [CONTROL_BORDER_WIDTH]: { label: "Size", type: "number", min: 0, units: "px" },
  [CONTROL_BORDER_RADIUS]: sharedControl(BORDER_RADIUS, { label: "Corner Radius" }),
  [CONTROL_PADDING]: { label: "Padding", type: "padding", min: 0, units: "px" },
  // The shadow control owns its own children, so it and they come from the shared config as a set.
  [CONTROL_SHADOW_STYLE]: sharedControl(SHADOW_STYLE, {
    children: [
      CONTROL_SHADOW_COLOR,
      CONTROL_SHADOW_OFFSET_X,
      CONTROL_SHADOW_OFFSET_Y,
      CONTROL_SHADOW_BLUR,
    ],
  }),
  [CONTROL_SHADOW_COLOR]: sharedControl(SHADOW_COLOR),
  [CONTROL_SHADOW_OFFSET_X]: sharedControl(SHADOW_OFFSET_X),
  [CONTROL_SHADOW_OFFSET_Y]: sharedControl(SHADOW_OFFSET_Y),
  [CONTROL_SHADOW_BLUR]: sharedControl(SHADOW_BLUR),
  // dropdown
  [CONTROL_ARROW_STYLE]: { label: "Style", type: "select", options: ARROW_STYLE_OPTIONS },
  [CONTROL_ARROW_COLOR]: { label: "Color", type: "color" },
  [CONTROL_ARROW_SIZE]: { label: "Size", type: "number", min: 0, units: "px" },
  // radio buttons
  [RADIO_DIRECTION]: { label: "Layout", type: "select", options: RADIO_LAYOUT_OPTIONS },
  [RADIO_GAP]: { label: "Spacing", type: "number", min: 0, units: "px" },
  [CONTROL_RADIO_STYLE]: {
    label: "Button Style",
    type: "select",
    options: RADIO_CHECKED_STYLE_OPTIONS,
  },
  [CONTROL_RADIO_DOT_COLOR]: { label: "Selected", type: "color" },
  [CONTROL_RADIO_DOT_SIZE]: {
    label: "Selected Size",
    type: "number",
    min: 0,
    units: "px",
  },
  // checkbox
  [CONTROL_CHECKBOX_STYLE]: {
    label: "Checked Style",
    type: "select",
    options: CHECKBOX_CHECKED_STYLE_OPTIONS,
  },
};

const FORM_FIELD_STATES = [FORM_STATE_FOCUS, FORM_STATE_ERROR];

/**
 * Registers each entry under its base prop code and under every state-prefixed form merge-utils
 * reads and writes (`focus.label.color`), so a lookup works the same whichever state is selected.
 * A composite control's children are prefixed too, or the shadow flyout would edit the base
 * shadow while the panel sits on Focus.
 * @param {Object<string, *>} entries
 * @returns {Object<string, *>}
 */
const withFormFieldStates = (entries) => ({
  ...entries,
  ...Object.fromEntries(
    FORM_FIELD_STATES.flatMap((state) =>
      Object.entries(entries).map(([propName, entry]) => [
        `${state}.${propName}`,
        entry.children
          ? { ...entry, children: entry.children.map((child) => `${state}.${child}`) }
          : entry,
      ]),
    ),
  ),
});

/** Every prop code the panel can address, keyed for every state. */
export const FORM_FIELD_PROPERTY_META_MAP = withFormFieldStates({
  ...BASE_PROPERTY_META_MAP,
  [CONTROL_BORDER_RADIUS]: {
    ...BASE_PROPERTY_META_MAP[CONTROL_BORDER_RADIUS],
    usePresets: false,
  },
});

const TEXT_FIELD_LABELS = {
  [CONTROL_TEXT_FONT]: "Text Font",
  [CONTROL_TEXT_SIZE]: "Text Size",
  [CONTROL_TEXT_COLOR]: "Text Color",
  [CONTROL_BORDER_STYLE]: "Field Border",
  [CONTROL_BORDER_WIDTH]: "Border Size",
  [CONTROL_PADDING]: "Inner Padding",
};

const DROPDOWN_LABELS = {
  ...TEXT_FIELD_LABELS,
  [CONTROL_ARROW_STYLE]: "Arrow Style",
  [CONTROL_ARROW_COLOR]: "Arrow Color",
  [CONTROL_ARROW_SIZE]: "Arrow Size",
};

const RADIO_LABELS = {
  [RADIO_DIRECTION]: "Option Layout",
  [RADIO_GAP]: "Option Spacing",
  [CONTROL_RADIO_DOT_COLOR]: "Selected Color",
  [CONTROL_TEXT_FONT]: "Text Font",
  [CONTROL_TEXT_SIZE]: "Text Size",
  [CONTROL_TEXT_COLOR]: "Text Color",
  [CONTROL_BORDER_STYLE]: "Button Border",
  [CONTROL_BORDER_WIDTH]: "Border Size",
  [CONTROL_PADDING]: "Inner Padding",
};

const CHECKBOX_LABELS = {
  ...TEXT_FIELD_LABELS,
  [CONTROL_TEXT_COLOR]: "Check Color",
  [CONTROL_BORDER_STYLE]: "Box Border",
};

const withLabels = (labels) =>
  withFormFieldStates(
    Object.fromEntries(
      Object.entries(BASE_PROPERTY_META_MAP).map(([propName, entry]) => [
        propName,
        labels[propName] ? { ...entry, label: labels[propName] } : entry,
      ]),
    ),
  );

export const FORM_FIELD_TEXT_FIELD_META_MAP = withLabels(TEXT_FIELD_LABELS);

export const FORM_FIELD_DROPDOWN_META_MAP = withLabels(DROPDOWN_LABELS);

export const FORM_FIELD_RADIO_META_MAP = withLabels(RADIO_LABELS);
export const FORM_FIELD_CHECKBOX_META_MAP = withLabels(CHECKBOX_LABELS);

/**
 * Theme settings keeps the generic labels, but its checkbox colour sits under Options with no Text
 * heading to say what it paints, so it borrows the element panel's Check Color name.
 */
export const FORM_FIELD_THEME_CHECKBOX_META_MAP = {
  ...FORM_FIELD_PROPERTY_META_MAP,
  ...withFormFieldStates({
    [CONTROL_TEXT_COLOR]: {
      ...BASE_PROPERTY_META_MAP[CONTROL_TEXT_COLOR],
      label: CHECKBOX_LABELS[CONTROL_TEXT_COLOR],
    },
  }),
};

/**
 * Flattens a seed layout into the dotted prop codes merge-utils addresses.
 * @param {Object} source
 * @param {string} prefix
 * @returns {Object<string, unknown>}
 */
const flattenPropValues = (source, prefix = "") =>
  Object.fromEntries(
    Object.entries(source).flatMap(([key, value]) => {
      const name = prefix ? `${prefix}.${key}` : key;

      return value !== null && typeof value === "object" && !Array.isArray(value)
        ? Object.entries(flattenPropValues(value, name))
        : [[name, value]];
    }),
  );

const FORM_STYLE_SEED = createFormStyleDefaults(DEFAULT_FORM_FONT);

/**
 * One seed entry's desktop values as dotted prop codes, with fonts set to the renderer's fallback.
 * @param {string} styleId
 * @returns {Object<string, unknown>}
 */
const seedValuesFor = (styleId) =>
  Object.fromEntries(
    Object.entries(flattenPropValues(FORM_STYLE_SEED[styleId].desktop)).map(([propName, value]) => [
      propName,
      FORM_FIELD_PROPERTY_META_MAP[propName]?.type === "font" ? FORM_FALLBACK_FONT : value,
    ]),
  );

const FIELD_SEED_VALUES = seedValuesFor(FORM_STYLE_FIELD);

const STATE_PREFIX = /^(focus|error)\./;

/**
 * Adds `focus.<k>` and `error.<k>` for every base key `k` the seed doesn't already set, so a state
 * prop whose live value resolves to nothing still shows its Default seed value.
 * @param {Object<string, unknown>} values
 * @returns {Object<string, unknown>}
 */
const withStateFallbacks = (values) => {
  const filled = { ...values };

  Object.entries(values).forEach(([propName, value]) => {
    if (STATE_PREFIX.test(propName)) return;
    ["focus", "error"].forEach((state) => {
      const stateProp = `${state}.${propName}`;
      if (!(stateProp in filled)) filled[stateProp] = value;
    });
  });

  return filled;
};

/**
 * Fallback shown when a style entry resolves nothing for a prop, taken from the same seed that
 * writes the theme so the panel cannot drift from it. A split prop the seed only sets on desktop
 * (`label.width`) would otherwise render blank on mobile. Every entry but `field` points at
 * `field`, so its own seed is laid over field's. A state prop the seed leaves out (most of them)
 * falls back to its base value, applied after that merge so an entry's own base value wins.
 */
export const DEFAULT_VALUES_BY_STYLE = Object.fromEntries(
  FORM_STYLE_IDS.map((styleId) => [
    styleId,
    withStateFallbacks({ ...FIELD_SEED_VALUES, ...seedValuesFor(styleId) }),
  ]),
);

export const FORM_FIELD_LABEL_PROPS = [
  LABEL_POSITION,
  LABEL_REQUIRED_PLACEMENT,
  LABEL_GAP,
  LABEL_WIDTH,
  LABEL_FONT,
  LABEL_SIZE,
  LABEL_COLOR,
  LABEL_REQUIRED_COLOR,
];

const FORM_FIELD_TEXT_PROPS = [CONTROL_TEXT_FONT, CONTROL_TEXT_SIZE, CONTROL_TEXT_COLOR];

const FORM_FIELD_CONTAINER_PROPS = [
  CONTROL_BACKGROUND_COLOR,
  CONTROL_BORDER_STYLE,
  CONTROL_BORDER_COLOR,
  CONTROL_BORDER_WIDTH,
  CONTROL_BORDER_RADIUS,
  CONTROL_PADDING,
  CONTROL_SHADOW_STYLE,
];

/** The Input Field section's controls, under the headings the panel groups them by. */
export const FORM_FIELD_INPUT_GROUPS = [
  { title: "Text", props: [...FORM_FIELD_TEXT_PROPS, CONTROL_TEXT_PLACEHOLDER_COLOR] },
  { title: "Container", props: FORM_FIELD_CONTAINER_PROPS },
];

/**
 * Drawn indented under the control above them. They stay their own props rather than becoming its
 * `children`, which would cost them the split control the seed's mobile `focus.bW` needs.
 */
export const FORM_FIELD_NESTED_INPUT_PROPS = [CONTROL_BORDER_COLOR, CONTROL_BORDER_WIDTH];

export const FORM_FIELD_INPUT_PROPS = FORM_FIELD_INPUT_GROUPS.flatMap(({ props }) => props);

const FORM_FIELD_LABEL_GROUP = { title: "Label", props: FORM_FIELD_LABEL_PROPS };

export const FORM_FIELD_TEXT_FIELD_GROUPS = [
  FORM_FIELD_LABEL_GROUP,
  { title: "Text", props: [...FORM_FIELD_TEXT_PROPS, CONTROL_TEXT_PLACEHOLDER_COLOR] },
  { title: "Field", props: FORM_FIELD_CONTAINER_PROPS },
];

const FORM_FIELD_ARROW_GROUP = {
  title: "Arrow",
  props: [CONTROL_ARROW_STYLE, CONTROL_ARROW_COLOR, CONTROL_ARROW_SIZE],
};

const FORM_FIELD_DROPDOWN_TEXT_GROUP = {
  title: "Text",
  props: [...FORM_FIELD_TEXT_PROPS, CONTROL_TEXT_PLACEHOLDER_COLOR],
};

export const FORM_FIELD_DROPDOWN_GROUPS = [
  FORM_FIELD_ARROW_GROUP,
  FORM_FIELD_LABEL_GROUP,
  FORM_FIELD_DROPDOWN_TEXT_GROUP,
  { title: "Container", props: FORM_FIELD_CONTAINER_PROPS },
];

export const FORM_FIELD_DROPDOWN_PANEL_GROUPS = [
  FORM_FIELD_ARROW_GROUP,
  FORM_FIELD_LABEL_GROUP,
  FORM_FIELD_DROPDOWN_TEXT_GROUP,
  { title: "Field", props: FORM_FIELD_CONTAINER_PROPS },
];

export const FORM_FIELD_DROPDOWN_PROPS = FORM_FIELD_DROPDOWN_GROUPS.flatMap(({ props }) => props);

export const FORM_FIELD_RADIO_GROUPS = [
  {
    title: "Options",
    props: [
      RADIO_DIRECTION,
      RADIO_GAP,
      CONTROL_RADIO_STYLE,
      CONTROL_RADIO_DOT_COLOR,
      CONTROL_RADIO_DOT_SIZE,
    ],
  },
  FORM_FIELD_LABEL_GROUP,
  { title: "Text", props: FORM_FIELD_TEXT_PROPS },
  {
    title: "Button",
    props: FORM_FIELD_CONTAINER_PROPS.filter((name) => name !== CONTROL_BORDER_RADIUS),
  },
];

export const FORM_FIELD_RADIO_PROPS = FORM_FIELD_RADIO_GROUPS.flatMap(({ props }) => props);

export const FORM_FIELD_CHECKBOX_GROUPS = [
  { title: "Options", props: [CONTROL_CHECKBOX_STYLE, CONTROL_TEXT_COLOR] },
  {
    ...FORM_FIELD_LABEL_GROUP,
    props: FORM_FIELD_LABEL_PROPS.filter((name) => name !== LABEL_WIDTH),
  },
  {
    title: "Box",
    props: FORM_FIELD_CONTAINER_PROPS.filter((name) => name !== CONTROL_PADDING),
  },
];

export const FORM_FIELD_CHECKBOX_PROPS = FORM_FIELD_CHECKBOX_GROUPS.flatMap(({ props }) => props);

/**
 * The Design Settings Dropdown tab: only what a dropdown keeps apart from the Label and Input Field
 * tabs, whose `field` entry it inherits the rest from. Padding stays because the dropdown stores its
 * own, widened on the right for the arrow.
 */
export const FORM_FIELD_THEME_DROPDOWN_GROUPS = [
  FORM_FIELD_ARROW_GROUP,
  { title: "Container", props: [CONTROL_PADDING] },
];

export const FORM_FIELD_THEME_DROPDOWN_PROPS = FORM_FIELD_THEME_DROPDOWN_GROUPS.flatMap(
  ({ props }) => props,
);

/** The Design Settings Radio Buttons tab: the radio's own groups, without the Label and Text it inherits. */
export const FORM_FIELD_THEME_RADIO_GROUPS = FORM_FIELD_RADIO_GROUPS.filter(
  ({ title }) => title !== FORM_FIELD_LABEL_GROUP.title && title !== "Text",
);

export const FORM_FIELD_THEME_RADIO_PROPS = FORM_FIELD_THEME_RADIO_GROUPS.flatMap(
  ({ props }) => props,
);

/**
 * The Design Settings Checkbox tab. Of the Label props it keeps only position and gap, which place
 * the label inline beside the box (checkboxes store `inline`); the rest it inherits from `field`.
 */
export const FORM_FIELD_THEME_CHECKBOX_GROUPS = FORM_FIELD_CHECKBOX_GROUPS.map((group) =>
  group.title === FORM_FIELD_LABEL_GROUP.title
    ? { ...group, props: [LABEL_POSITION, LABEL_GAP] }
    : group,
);

export const FORM_FIELD_THEME_CHECKBOX_PROPS = FORM_FIELD_THEME_CHECKBOX_GROUPS.flatMap(
  ({ props }) => props,
);

/**
 * The props each type's theme entry may store: what its Design Settings tab shows, with a composite's
 * children, plus what its seed sets (the radios' round border isn't on their tab). Anything else was
 * written by a tab that used to show it, and would beat the `field` value with no control to change it.
 */
export const FORM_STYLE_OWN_PROPS = Object.fromEntries(
  [
    [FORM_STYLE_DROPDOWN, FORM_FIELD_THEME_DROPDOWN_PROPS],
    [FORM_STYLE_RADIOS, FORM_FIELD_THEME_RADIO_PROPS],
    [FORM_STYLE_CHECKBOXES, FORM_FIELD_THEME_CHECKBOX_PROPS],
  ].map(([styleId, tabProps]) => [
    styleId,
    [
      ...new Set([
        ...tabProps.flatMap((prop) => [
          prop,
          ...(FORM_FIELD_PROPERTY_META_MAP[prop]?.children ?? []),
        ]),
        ...Object.keys(seedValuesFor(styleId)),
      ]),
    ],
  ]),
);

/**
 * Prefixes a prop name with the field state it belongs to, mirroring the `hover.` prefix buttons use.
 * The default state addresses the base prop, so it is returned unchanged.
 * @param {string} propName - Base merge-utils prop code, e.g. `label.color`.
 * @param {import("@/constants/FormFieldConstants.mjs").FormState} state
 * @returns {string} The prop code to read and write for that state.
 */
export const toStateProp = (propName, state) =>
  state === FORM_STATE_DEFAULT ? propName : `${state}.${propName}`;

/**
 * The prop codes a section renders for one state: structural props drop out on focus and error,
 * and what is left is addressed under that state.
 * @param {string[]} propNames - The section's full prop list.
 * @param {import("@/constants/FormFieldConstants.mjs").FormState} state
 * @returns {string[]} State-addressed prop codes, e.g. `focus.label.color`.
 */
export const getFormFieldStateProps = (propNames, state) =>
  propNames
    .filter((name) => state === FORM_STATE_DEFAULT || !FORM_FIELD_STRUCTURAL_PROPS.includes(name))
    .map((name) => toStateProp(name, state));

/**
 * Drops the controls a field cannot show: Label Width only applies to an inline label.
 * @param {string[]} propNames - Base prop codes, before `getFormFieldStateProps`.
 * @param {Object} field
 * @param {string=} field.labelPosition - The field's resolved `label.position`.
 * @returns {string[]}
 */
export const filterApplicableFormFieldProps = (propNames, { labelPosition }) =>
  propNames.filter(
    (name) => name !== LABEL_WIDTH || labelPosition === FORM_FIELD_LABEL_POSITION_INLINE,
  );

/**
 * Turns one `mapProp` result into the propData a control renders from: the meta it reads its type
 * and options off, the theme palette, and a handler on every event a control can emit. Every
 * handler in `ALL_HANDLERS` is wired because ColorPicker routes its swatch row to `onSIChange` but
 * its body and alpha slider to `onSIInput`. Composite controls such as the shadow flyout render
 * their children through the same path, so those are wired too.
 * @param {import("./ButtonPropertyMapper").MappedElementLayout} mapped - One `mapProp` result.
 * @param {Object} context
 * @param {Function} context.onFormatChange - `useStyleSync`'s change formatter.
 * @param {Array=} context.palette - Theme colors for the colour controls.
 * @returns {Object} propData
 */
export const toControlProps = ({ controlMeta, subProps = {}, ...propData }, context) => {
  const { onFormatChange, palette } = context;
  const { value } = propData;

  // An alpha change is applied to the displayed colour; empty storage would tint an unrelated one.
  const handler = onFormatChange(propData.name, value, false);

  return {
    ...propData,
    ...controlMeta,
    customPalette: palette,
    subProps: Object.fromEntries(
      Object.entries(subProps).map(([name, subProp]) => [name, toControlProps(subProp, context)]),
    ),
    inputEventHandlers: ALL_HANDLERS,
    ...Object.fromEntries(ALL_HANDLERS.map((handlerName) => [handlerName, handler])),
  };
};

/**
 * Builds the propData one form field control renders from. The shared element mapper supplies value
 * resolution, the seed-default fallback, split/merge routing and the context menu; the panel's
 * `useStyleSync` supplies the handlers.
 * @param {Object} params
 * @param {import("immutable").Map} params.element - The theme style entry being edited.
 * @param {import("immutable").Map} params.theme - The theme, used as the inheritance source.
 * @param {string} params.layout - `LAYOUT_DESKTOP` or `LAYOUT_MOBILE`.
 * @param {string} params.prop - State-addressed prop code, e.g. `focus.label.color`.
 * @param {Object} params.styleSync - The panel's `useStyleSync` result.
 * @param {Array=} params.palette - Theme colors for the colour controls.
 * @param {Object<string, *>=} params.metaMap - The controls' labels and types; defaults to the generic map.
 * @returns {Object} propData
 */
export const buildFormFieldPropData = ({
  element,
  theme,
  layout,
  prop,
  styleSync,
  palette,
  metaMap = FORM_FIELD_PROPERTY_META_MAP,
}) => {
  const { addToggle, isSynced, onFormatChange } = styleSync;

  return toControlProps(
    mapProp({
      element,
      canMerge: true,
      prop,
      layout,
      isSynced,
      theme,
      // Merging carries the displayed value across, so a layout storing nothing cannot clear the other.
      toggleSyncedProp: (toggledProp, mergeValue) =>
        addToggle(toggledProp, mergeValue).toggleSync(),
      metaMap,
      resolveValue: resolveFormFieldValue,
      defaultValues: DEFAULT_VALUES_BY_STYLE[element.get("id")],
    }),
    { onFormatChange, palette },
  );
};

/** True when the entry stores the prop, or for a composite any of its children, on neither layout. */
const isUnsetOnBothLayouts = (element, prop) =>
  [prop, ...(FORM_FIELD_PROPERTY_META_MAP[prop]?.children ?? [])].every((name) =>
    [LAYOUT_DESKTOP, LAYOUT_MOBILE].every((layout) => getProp(element, layout, name) === undefined),
  );

/**
 * True when the prop is purely inherited from a merged tier. A state prop (`focus.*`, `error.*`)
 * nothing in the chain sets shows its Default value, so it also counts when its base prop is synced,
 * unless the entry already lists it.
 */
const inheritsSyncedProp = (element, theme, layout, prop) => {
  if (
    !isPropSynced(element, prop) &&
    isUnsetOnBothLayouts(element, prop) &&
    canPropInherit(element, prop) &&
    isInheritedPropSynced(element, layout, prop, theme)
  ) {
    return true;
  }

  const base = prop.replace(STATE_PREFIX, "");

  return (
    base !== prop &&
    !isPropSynced(element, prop) &&
    isUnsetOnBothLayouts(element, prop) &&
    inheritProp(element, layout, prop, theme) === undefined &&
    (isPropSynced(element, base) || inheritsSyncedProp(element, theme, layout, base))
  );
};

/** Adds first-edited inherited merged props to the entry's sync list. */
const keepInheritedSync = (element, theme, pathPrefix, propInfo) => {
  const updates = ensureArray(propInfo);
  const desktopPrefix = `${pathPrefix}${LAYOUT_DESKTOP}.`;
  const adopted = [
    ...new Set(
      updates
        .filter(({ name }) => name.startsWith(desktopPrefix))
        .map(({ name }) => name.slice(desktopPrefix.length))
        .map((prop) => getSubParent(element, prop) ?? prop),
    ),
  ].filter((prop) => inheritsSyncedProp(element, theme, LAYOUT_DESKTOP, prop));

  return adopted.length
    ? [
        ...updates,
        {
          name: `${pathPrefix}${SYNCED_PROPS}`,
          value: element.get(SYNCED_PROPS, List()).concat(adopted),
        },
      ]
    : propInfo;
};

/** Wraps useStyleSync so inherited props split and merge correctly. */
export const withInheritedSync = (styleSync, { element, theme, layout, onChange }) => {
  const pathPrefix = `${THEME_STYLES}.${element.get("id")}.`;
  const inherits = (prop) =>
    !styleSync.isSynced(prop) &&
    inheritsSyncedProp(element, theme, layout, getSubParent(element, prop) ?? prop);
  const isSynced = (prop) => styleSync.isSynced(prop) || inherits(prop);
  const { onChange: onFormatChange } = initFormatter(
    (propInfo) => onChange(keepInheritedSync(element, theme, pathPrefix, propInfo)),
    (prop) => `${pathPrefix}${isSynced(prop) ? LAYOUT_DESKTOP : layout}.${prop}`,
  );
  const groupMergeValue = (parent) => {
    const path = parent.split(".");
    const desktopValue = element.getIn([LAYOUT_DESKTOP, ...path]);
    const layoutValue = element.getIn([layout, ...path]);

    return desktopValue && layoutValue ? desktopValue.merge(layoutValue) : layoutValue;
  };
  const addToggle = (prop, mergeValue) => {
    const parent = getSubParent(element, prop);

    if (parent) return styleSync.addToggle(parent, groupMergeValue(parent));

    if (inherits(prop) || (styleSync.isSynced(prop) && isUnsetOnBothLayouts(element, prop))) {
      // Splitting a synced prop nothing stores gives each layout the value it displays, and drops
      // it from the sync list so the state fallback does not report it synced again. A state prop
      // composes over its own layout's Default, so the layouts can differ when Default is split.
      return {
        toggleSync: () => {
          const synced = element.get(SYNCED_PROPS, List());
          const defaults = DEFAULT_VALUES_BY_STYLE[element.get("id")] ?? {};
          const updates = [LAYOUT_DESKTOP, LAYOUT_MOBILE].map((splitLayout) => ({
            name: `${pathPrefix}${splitLayout}.${prop}`,
            value:
              splitLayout === layout
                ? mergeValue
                : (resolveFormFieldValue(element, splitLayout, prop, theme) ?? defaults[prop]),
          }));

          onChange(
            synced.includes(prop)
              ? [
                  ...updates,
                  {
                    name: `${pathPrefix}${SYNCED_PROPS}`,
                    value: synced.filter((name) => name !== prop),
                  },
                ]
              : updates,
          );
        },
      };
    }

    return styleSync.addToggle(prop, mergeValue);
  };

  return { ...styleSync, addToggle, isSynced, onFormatChange };
};
